'use client';

import { createContext, useContext, useEffect } from 'react';

export type PromoBannerSlotRegistration = {
    id: string;
    intervalMs: number;
};

export type PromoBannerRotatorContextValue = {
    register: (registration: PromoBannerSlotRegistration) => () => void;
    /** `null` shows every slot: pre-hydration, design mode, or outside a rotator. */
    activeId: string | null;
};

export const PromoBannerRotatorContext = createContext<PromoBannerRotatorContextValue | null>(null);

/**
 * Registers a banner with the rotator and reports whether it is on screen.
 *
 * `<Region>` maps its components internally, so banners register themselves rather than
 * being enumerated. Effects run in mount order, giving DOM order.
 */
export function usePromoBannerSlot(id: string | undefined, intervalMs: number): { isActive: boolean } {
    const context = useContext(PromoBannerRotatorContext);
    const register = context?.register;

    useEffect(() => {
        if (!register || !id) {
            return;
        }
        return register({ id, intervalMs });
    }, [id, intervalMs, register]);

    if (!context || !id || context.activeId === null) {
        return { isActive: true };
    }

    return { isActive: context.activeId === id };
}
